import type { SessionsState } from './types';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { sessionsApi } from './api';

const INITIAL_STATE: SessionsState = {
  current_session: {
    id: null,
    section_id: null,
    question_id: null,
    started_at: null,
    finished_at: null,
    completed: 0,
    package: null,
    sections: [],
    remaining_time: 0,
    redirect_url: null,
  },
  current_question_response: null,
  isLoadingQuestionResponse: false,
};

const sessionsSlice = createSlice({
  name: 'sessions',
  initialState: INITIAL_STATE,
  reducers: {
    updateSectionId(state, action: PayloadAction<number | null>) {
      state.current_session.section_id = action.payload;
    },
    updateQuestionId(state, action: PayloadAction<number | null>) {
      state.current_session.question_id = action.payload;
    },
    // Optimistically mark a question Completed the moment an answer is chosen,
    // so a live subset predicate (e.g. Review Incomplete) drops it immediately
    // instead of waiting for the answer PUT to resolve. The authoritative
    // status still arrives via putQuestionResponse.matchFulfilled.
    optimisticCompleteQuestion(state, action: PayloadAction<number>) {
      const section = state.current_session.sections.find((s) =>
        s.questions.some((q) => q.id === action.payload),
      );
      const question = section?.questions.find((q) => q.id === action.payload);
      if (question) question.status = 'Completed';
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(sessionsApi.endpoints.startSession.matchFulfilled, (state, { payload }) => {
      state.current_session.id = payload.id;
    });
    builder.addMatcher(sessionsApi.endpoints.getSession.matchFulfilled, (state, { payload, meta }) => {
      state.current_session = { ...payload, id: meta.arg.originalArgs.session_id };
    });
    builder.addMatcher(sessionsApi.endpoints.getQuestionResponse.matchPending, (state) => {
      state.isLoadingQuestionResponse = true;
    });
    builder.addMatcher(sessionsApi.endpoints.getQuestionResponse.matchFulfilled, (state, { payload }) => {
      state.current_session = { ...state.current_session, question_id: payload.question_id };
      state.current_question_response = { ...payload };
      state.isLoadingQuestionResponse = false;

      // Update the section questions array so the Navigator reflects current status
      const section = state.current_session.sections.find((s) => s.questions.some((q) => q.id === payload.question_id));
      if (section) {
        const question = section.questions.find((q) => q.id === payload.question_id);
        if (question) {
          question.status = payload.value ? 'Completed' : 'InComplete';
          question.flagged = payload.flagged;
        }
      }
    });
    builder.addMatcher(sessionsApi.endpoints.getQuestionResponse.matchRejected, (state) => {
      state.isLoadingQuestionResponse = false;
    });
    builder.addMatcher(sessionsApi.endpoints.putQuestionResponse.matchFulfilled, (state, { payload }) => {
      // Only repaint the on-screen response if this PUT belongs to the question
      // currently displayed. A late-resolving PUT for a question the user has
      // already navigated away from must NOT stamp its value onto the new
      // question's view — that contaminated value can then be persisted by a
      // later flag toggle, corrupting an unanswered question (see issue #44).
      if (state.current_question_response && payload.question_id === state.current_session.question_id) {
        state.current_question_response = {
          ...state.current_question_response,
          value: payload.value,
          flagged: payload.flagged ? 1 : 0,
        };
      }

      // Update the section questions array so the Navigator reflects current status.
      // Keyed by payload.question_id, so this is correct even for an out-of-order
      // PUT — the answered question still registers regardless of what's on screen.
      const section = state.current_session.sections.find((s) => s.questions.some((q) => q.id === payload.question_id));
      if (section) {
        const question = section.questions.find((q) => q.id === payload.question_id);
        if (question) {
          question.status = payload.value ? 'Completed' : 'InComplete';
          question.flagged = payload.flagged ? 1 : 0;
        }
      }
    });
    builder.addMatcher(sessionsApi.endpoints.putSelection.matchFulfilled, (state) => {
      state.current_question_response = null;
    });
  },
});

export const { updateSectionId, updateQuestionId, optimisticCompleteQuestion } = sessionsSlice.actions;
export default sessionsSlice.reducer;
